import { useState } from "react";

import { RateComponent, SupplierQuote } from "../../../types/proposalDocuments.types";
import {
  getQuoteAnchorId,
  getQuoteSummary,
  getSectionAnchorId,
} from "../../../utils/proposalDocuments/quoteOutline";
import {
  MeterFuelType,
  getMeterFuelType,
  getMeterNumber,
} from "../../../utils/proposalDocuments/relatedMeters";
import { computeRateCost, isCommodityConsumptionRate } from "../proposalDocuments.constants";
import { useProposalDocument } from "../proposalDocumentsContext";

import CalculatedField from "./CalculatedField";
import { makeEmptyRate } from "./quoteSection";
import RateRow from "./rateRow";
import { useCollapsible } from "./useCollapsible";

interface SupplierQuoteCardProps {
  quote: SupplierQuote;
  index?: number;
  onChange: (updated: SupplierQuote) => void;
  onRemove: () => void;
  sectionIndex: number | "current";
  isCurrent: boolean;
}

const SupplierQuoteCard: React.FC<SupplierQuoteCardProps> = ({
  quote,
  index,
  onChange,
  onRemove,
  sectionIndex,
  isCurrent,
}) => {
  const { fieldErrors, relatedMeters } = useProposalDocument();
  const optionNumber = (index ?? 0) + 1;

  const base = getQuoteAnchorId(sectionIndex, index ?? 0);

  const getFieldKey = (field: string) => `${base}-${field}`;

  const getRateKey = (rIndex: number) => `${base}-rate-${rIndex.toString()}`;

  // When rate is updated
  const computeEstimatedAnnualCost = (rates: RateComponent[]) => {
    const ratesTotal = rates.reduce((sum, r) => sum + (r.estimatedAnnualCost || 0), 0);
    return parseFloat(ratesTotal.toFixed(2));
  };

  const computeAnnualConsumption = (rates: RateComponent[]) =>
    rates.filter(isCommodityConsumptionRate).reduce((sum, r) => sum + (r.consumption || 0), 0);

  // Non-commodity (pass-through) rates don't have their own consumption -
  // theirs is pegged to the total consumption of the commodity (p/kWh)
  // rates in the same quote, and recomputed any time the rates array
  // changes. KVA is deliberately excluded - its consumption is a kVA
  // capacity figure, not kWh usage.
  const recalculateNonCommodityRates = (rates: RateComponent[]): RateComponent[] => {
    const commodityConsumption = rates
      .filter(isCommodityConsumptionRate)
      .reduce((sum, r) => sum + (r.consumption || 0), 0);

    return rates.map((r) =>
      r.hasConsumption && r.nonCommodity
        ? {
            ...r,
            consumption: commodityConsumption,
            estimatedAnnualCost: computeRateCost(commodityConsumption, r.value, r.unit),
          }
        : r,
    );
  };

  // Which meter field is "active" - tracked locally rather than derived
  // from quote.mpan/mprn, since both now default to null until a meter
  // is picked from the list (can't infer the intended fuel from "both null")
  const [meterType, setMeterType] = useState<"mpan" | "mprn">(quote.mprn ? "mprn" : "mpan");
  const meterNumberKey = meterType === "mpan" ? `${base}-mpan` : `${base}-mprn`;

  // Any error keyed under this quote's `base` prefix — used to highlight
  // the whole card and auto-expand it so a validation failure isn't hidden
  // inside a collapsed option
  const quoteHasError = Object.keys(fieldErrors).some((k) => k.startsWith(`${base}-`));

  const { isCollapsed, toggle } = useCollapsible({
    id: base,
    hasError: quoteHasError,
    groupId: getSectionAnchorId(sectionIndex),
  });

  // Quick-glance summary shown next to the option title, so collapsing
  // a card doesn't hide the info someone's scanning the list for
  const summaryParts = getQuoteSummary(quote);

  // Meters are restricted to the customer's fetched Zoho meters, matching
  // the selected fuel type - no free-text meter numbers anymore
  const candidateFuelType: MeterFuelType = meterType === "mpan" ? "electricity" : "gas";
  const candidateMeters = relatedMeters.filter((m) => getMeterFuelType(m) === candidateFuelType);

  const handleMeterTypeChange = (type: "mpan" | "mprn") => {
    setMeterType(type);
    onChange({ ...quote, mpan: null, mprn: null, zoho_meter_id: null });
  };

  // Selecting from the list has to set both the meter number (what the
  // backend/PDF display uses) and the Zoho meter id (what the backend
  // needs to link the record back to the CRM)
  const handleMeterSelect = (meterId: string) => {
    if (meterId === "") {
      onChange({ ...quote, [meterType]: null, zoho_meter_id: null });
      return;
    }
    const meter = candidateMeters.find((m) => m.id === meterId);
    onChange({
      ...quote,
      [meterType]: meter ? getMeterNumber(meter, candidateFuelType) : null,
      zoho_meter_id: meterId,
    });
  };

  // Local display state for the raw entry field, kept in sync with
  // `quote.uplift` during render below rather than bound directly to it -
  // mirrors the pattern in rateRow.tsx, keeping the input controlled (so
  // external updates are reflected) without fighting the user mid-keystroke.
  const [upliftInput, setUpliftInput] = useState(String(quote.uplift));

  const [prevUplift, setPrevUplift] = useState(quote.uplift);

  if (quote.uplift !== prevUplift) {
    setPrevUplift(quote.uplift);
    if (Number(upliftInput) !== quote.uplift) setUpliftInput(String(quote.uplift));
  }

  const handleUpliftChange = (value: string) => {
    setUpliftInput(value);
    const newUplift = value === "" || Number.isNaN(Number(value)) ? 0 : Number(value);
    onChange({
      ...quote,
      uplift: newUplift,
    });
  };

  const handleNotesChange = (value: string) => {
    onChange({ ...quote, notes: value === "" ? null : value });
  };

  const handleUpdateRate = (rIndex: number, updated: RateComponent) => {
    const newRates = recalculateNonCommodityRates(
      quote.rates.map((r, i) => (i === rIndex ? updated : r)),
    );
    onChange({
      ...quote,
      rates: newRates,
      estimated_annual_cost: computeEstimatedAnnualCost(newRates),
      annual_consumption: computeAnnualConsumption(newRates),
    });
  };

  const handleRemoveRate = (rIndex: number) => {
    const newRates = [...quote.rates];
    newRates.splice(rIndex, 1);
    const recalculatedRates = recalculateNonCommodityRates(newRates);
    onChange({
      ...quote,
      rates: recalculatedRates,
      estimated_annual_cost: computeEstimatedAnnualCost(recalculatedRates),
      annual_consumption: computeAnnualConsumption(recalculatedRates),
    });
  };

  const handleAddRate = () => {
    const newRates = recalculateNonCommodityRates([...quote.rates, makeEmptyRate()]);
    onChange({
      ...quote,
      rates: newRates,
      estimated_annual_cost: computeEstimatedAnnualCost(newRates),
      annual_consumption: computeAnnualConsumption(newRates),
    });
  };

  return (
    <article
      id={base}
      className={`supplier-quote-card ${isCurrent ? "supplier-quote-card--current" : "supplier-quote-card--proposed"} ${quoteHasError ? "supplier-quote-card--has-error" : ""}`}
    >
      {/* Header */}
      <div className="supplier-quote-card__header">
        <button
          type="button"
          className={`supplier-quote-card__collapse-button ${isCollapsed ? "is-collapsed" : ""}`}
          onClick={toggle}
          aria-label={isCollapsed ? "Expand option" : "Collapse option"}
          aria-expanded={!isCollapsed}
        >
          <span className="collapse-icon" aria-hidden="true" />
        </button>

        <div className="supplier-quote-card__title">
          <h3>Option #{optionNumber}</h3>
          {summaryParts.length > 0 && (
            <span className="supplier-quote-card__summary">{summaryParts.join(" · ")}</span>
          )}
        </div>

        <button
          type="button"
          className="supplier-quote-card__remove-button"
          onClick={onRemove}
          aria-label={`Remove proposed quote option ${optionNumber.toString()}`}
        >
          Remove Option
        </button>
      </div>

      {!isCollapsed && (
        <>
          {/* Meter Details */}
          <div className="supplier-quote-card__meter-details">
            <div className="supplier-quote-card__meter-details-row">
              <div className="supplier-quote-card__field-group">
                <label htmlFor={`meter-type-${String(sectionIndex)}-${String(index)}`}>
                  Meter Type
                </label>
                <select
                  id={`meter-type-${String(sectionIndex)}-${String(index)}`}
                  className="supplier-quote-card__field-input"
                  value={meterType}
                  onChange={(e) => {
                    handleMeterTypeChange(e.target.value as "mpan" | "mprn");
                  }}
                  aria-label="Meter type"
                >
                  <option value="mpan">MPAN (Electricity)</option>
                  <option value="mprn">MPRN (Gas)</option>
                </select>
              </div>

              <div className="supplier-quote-card__field-group">
                <label htmlFor={`meter-number-${String(sectionIndex)}-${String(index)}`}>
                  {meterType === "mpan" ? "MPAN" : "MPRN"} Meter
                </label>
                <select
                  id={`meter-number-${String(sectionIndex)}-${String(index)}`}
                  className={`supplier-quote-card__field-input ${fieldErrors[meterNumberKey] ? "is-invalid" : ""}`}
                  value={quote.zoho_meter_id ?? ""}
                  onChange={(e) => {
                    handleMeterSelect(e.target.value);
                  }}
                  aria-label={meterType === "mpan" ? "MPAN meter" : "MPRN meter"}
                >
                  <option value="">-- Select a meter --</option>
                  {candidateMeters.map((m) => {
                    const number = getMeterNumber(m, candidateFuelType);
                    if (!number) return null;
                    return (
                      <option key={m.id} value={m.id}>
                        {meterType === "mpan" ? "MPAN" : "MPRN"} {number}
                      </option>
                    );
                  })}
                </select>
                {fieldErrors[meterNumberKey] && (
                  <div className="invalid-feedback d-block">{fieldErrors[meterNumberKey]}</div>
                )}
              </div>
            </div>
          </div>

          {/* Cost / Uplift */}
          <div className="supplier-quote-card__fields">
            <CalculatedField
              id={getFieldKey("annual_consumption")}
              label="Annual Consumption"
              value={quote.annual_consumption ?? 0}
              suffix="kWh"
              decimals={0}
            />

            <CalculatedField
              id={getFieldKey("estimated_annual_cost")}
              label="Estimated Annual Cost"
              value={quote.estimated_annual_cost ?? 0}
              prefix="£"
            />

            <div className="supplier-quote-card__field-group">
              <label htmlFor={getFieldKey("uplift")}>
                Uplift p/kWh {isCurrent && "(optional)"}
              </label>
              <input
                id={getFieldKey("uplift")}
                type="number"
                placeholder="e.g. 12"
                value={upliftInput}
                onChange={(e) => {
                  handleUpliftChange(e.target.value);
                }}
                className={`form-control ${fieldErrors[getFieldKey("uplift")] ? "is-invalid" : ""}`}
              />
              {fieldErrors[getFieldKey("uplift")] && (
                <div className="invalid-feedback d-block">{fieldErrors[getFieldKey("uplift")]}</div>
              )}
            </div>
          </div>

          {/* Notes */}
          <div className="supplier-quote-card__details">
            <div className="supplier-quote-card__field-group">
              <label htmlFor={`proposed-notes-${String(index)}`}>Notes</label>
              <textarea
                id={`proposed-notes-${String(index)}`}
                className="supplier-quote-card__notes-textarea"
                placeholder="e.g. Subject to credit check"
                value={quote.notes ?? ""}
                onChange={(e) => {
                  handleNotesChange(e.target.value);
                }}
              />
            </div>
          </div>

          {/* Rates Section */}
          <div className="supplier-quote-card__rates">
            <span className="rates-label">Rates:</span>
            {fieldErrors[getFieldKey("rates")] && (
              <div className="invalid-feedback d-block">{fieldErrors[getFieldKey("rates")]}</div>
            )}
            <div className="supplier-quote-card__rates-list">
              {quote.rates.map((rateComponent, rIndex) => (
                <RateRow
                  key={rIndex}
                  rate={rateComponent}
                  errorMessage={fieldErrors[getRateKey(rIndex)]}
                  onChange={(updated) => {
                    handleUpdateRate(rIndex, updated);
                  }}
                  onRemove={() => {
                    handleRemoveRate(rIndex);
                  }}
                />
              ))}
            </div>
            <button
              type="button"
              className="supplier-quote-card__add-rate-button"
              onClick={handleAddRate}
              aria-label="Add new rate"
            >
              + Add Rate
            </button>
          </div>
        </>
      )}
    </article>
  );
};

export default SupplierQuoteCard;
